<script setup>
import {onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import ServiceRequirementService from '@/services/ServiceRequirementService';
import {formatDate, showSuccessMessage, getCssVar, getContrastColor} from '@/helper';
import {useI18n} from 'vue-i18n';
import Button from '@/components/forms/Button.vue';

const {t} = useI18n();
const router = useRouter();
const serviceRequirementId = router.currentRoute.value.params.id;

const serviceRequirement = ref({});
const isLoading = ref(true);
const isSending = ref(false);
// Stavy změny statusu — spinner při čekání na odpověď, fajfka po úspěchu
const isChangingStatus = ref(false);
const statusSaved = ref(false);

// Formulář nového komentáře pro BO
const newCommentText = ref('');
const newCommentType = ref('EXTERNAL');
const newCommentFiles = ref([]);
const commentFileInput = ref(null);

const statusOptions = [
  {value: 'NEW', label: 'Nový'},
  {value: 'IN_PROGRESS', label: 'V řešení'},
  {value: 'WAITING_FOR_CLIENT', label: 'Čeká na klienta'},
  {value: 'WAITING_FOR_BO', label: 'Čeká na BO'},
  {value: 'CLOSED', label: 'Uzavřeno'},
];

onMounted(() => {
  fetchServiceRequirement();
});

function fetchServiceRequirement() {
  isLoading.value = true;
  ServiceRequirementService.getServiceRequirement(serviceRequirementId).then(data => {
    serviceRequirement.value = data;
  }).finally(() => {
    isLoading.value = false;
  });
}

function processServiceRequirement() {
  ServiceRequirementService.processServiceRequirement(serviceRequirementId).then(() => {
    showSuccessMessage(t('serviceRequirementDetail.service_request_handled'));
    fetchServiceRequirement();
  });
}

function resolveRequirement() {
  ServiceRequirementService.resolveServiceRequirement(serviceRequirementId).then(data => {
    serviceRequirement.value = data;
    showSuccessMessage(t('serviceRequirementDetail.resolved'));
  });
}

function reopenRequirement() {
  ServiceRequirementService.reopenServiceRequirement(serviceRequirementId).then(data => {
    serviceRequirement.value = data;
    showSuccessMessage(t('serviceRequirementDetail.reopened'));
  });
}

function changeStatus(status) {
  isChangingStatus.value = true;
  statusSaved.value = false;
  ServiceRequirementService.updateStatus(serviceRequirementId, status).then(data => {
    serviceRequirement.value = data;
    statusSaved.value = true;
    // Fajfka zmizí po 2 sekundách
    setTimeout(() => { statusSaved.value = false; }, 2000);
  }).finally(() => {
    isChangingStatus.value = false;
  });
}

function handleFileChange(e) {
  newCommentFiles.value = Array.from(e.target.files);
}

function sendComment() {
  if (!newCommentText.value.trim()) return;
  isSending.value = true;

  const formData = new FormData();
  formData.append('text', newCommentText.value);
  formData.append('type', newCommentType.value);
  newCommentFiles.value.forEach(file => formData.append('attachments[]', file));

  ServiceRequirementService.addComment(serviceRequirementId, formData).then(comment => {
    if (!serviceRequirement.value.comments) serviceRequirement.value.comments = [];
    serviceRequirement.value.comments.push(comment);
    // Aktualizujeme status pokud ho backend změnil (komentář ho mohl ovlivnit)
    fetchServiceRequirement();
    newCommentText.value = '';
    newCommentType.value = 'EXTERNAL';
    newCommentFiles.value = [];
    if (commentFileInput.value) commentFileInput.value.value = '';
    showSuccessMessage(t('serviceRequirementDetail.comment_sent'));
  }).finally(() => {
    isSending.value = false;
  });
}

/** Vrací inline styl pro badge statusu — barva textu je vypočtena dle WCAG kontrastu. */
function statusBadgeStyle(status) {
  const bg1 = getCssVar('--primaryColor', '#004148');
  const bg2 = getCssVar('--primaryHoverColor', '#046e7a');
  const primary = {color: getContrastColor(bg1), backgroundColor: bg1, borderColor: bg1};
  const hover = {color: getContrastColor(bg2), backgroundColor: bg2, borderColor: bg2};
  const light = {color: bg1, backgroundColor: '#f0f2f4', borderColor: '#f0f2f4'};
  const map = {
    NEW: light,
    IN_PROGRESS: primary,
    WAITING_FOR_CLIENT: hover,
    WAITING_FOR_BO: light,
    CLOSED: primary,
  };
  return map[status] ?? light;
}

function statusLabel(status) {
  return statusOptions.find(o => o.value === status)?.label ?? status;
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-3">
          <a @click="router.back()" class="page-section__heading__back">
            <i class="mdi mdi-arrow-left"></i> Zpět
          </a>
        </div>
        <div class="col-5 text-center">
          <div class="page-section__title">{{ t('serviceRequirementDetail.service_request_detail') }}</div>
        </div>
      </div>
    </div>

    <div class="page-section__content">
      <div class="mb-4">
        <div class="row gy-3">
          <!-- Základní info + Dotaz -->
          <div class="col-12 col-md-6">
            <div class="card mb-3">
              <div class="card-header">
                <div class="card-header__title">{{ t('serviceRequirementDetail.basic_data') }}</div>
              </div>
              <div class="card-body">
                <div class="info-table">
                  <div class="info-table__row row" v-if="serviceRequirement.client">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.client:') }}</span>
                    <span class="info-table__value col-12">{{ serviceRequirement.client.label }}</span>
                  </div>
                  <!-- E-mail a telefon klienta — zobrazí se pouze pokud BE tato pole vrátí -->
                  <div class="info-table__row row" v-if="serviceRequirement.client?.email">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.email:') }}</span>
                    <span class="info-table__value col-12">
                      <a :href="'mailto:' + serviceRequirement.client.email">{{ serviceRequirement.client.email }}</a>
                    </span>
                  </div>
                  <div class="info-table__row row" v-if="serviceRequirement.client?.phone">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.phone:') }}</span>
                    <span class="info-table__value col-12">
                      <a :href="'tel:' + serviceRequirement.client.phone">{{ serviceRequirement.client.phone }}</a>
                    </span>
                  </div>
                  <!-- Autor: BO uživatel pokud vytvořil BO, jinak klient/poradce -->
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.author:') }}</span>
                    <span class="info-table__value col-12">
                      {{ (serviceRequirement.request_user ?? serviceRequirement.user)?.label }}
                    </span>
                  </div>
                  <!-- Poradce: zobrazí se jen pokud je požadavek adresován konkrétnímu poradci -->
                  <div class="info-table__row row" v-if="serviceRequirement.request_user && serviceRequirement.user">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.advisor:') }}</span>
                    <span class="info-table__value col-12">{{ serviceRequirement.user?.label }}</span>
                  </div>
                  <!-- Vyžádáno z back-office: jen pokud je autorem uživatel s rolí BACK_OFFICE -->
                  <div class="info-table__row row" v-if="serviceRequirement.request_user?.role === 'BACK_OFFICE'">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.requested_from_backoffice:') }}</span>
                    <span class="info-table__value col-12">{{ serviceRequirement.request_user.label }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.creation_date:') }}</span>
                    <span class="info-table__value col-12">{{ formatDate(serviceRequirement.created_at, 'd.m.Y H:i') }}</span>
                  </div>
                  <div class="info-table__row row" v-if="serviceRequirement.request_text">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.message_from_backoffice:') }}</span>
                    <span class="info-table__value col-12">{{ serviceRequirement.request_text }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="card">
              <div class="card-header">
                <div class="card-header__title">{{ t('serviceRequirementDetail.inquiry') }}</div>
              </div>
              <div class="card-body">
                <div class="info-table">
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.type:') }}</span>
                    <span class="info-table__value col-12">{{ t('serviceRequirements.' + serviceRequirement.type, serviceRequirement.type) }}</span>
                  </div>
                  <div class="info-table__row row">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.message:') }}</span>
                    <span class="info-table__value col-12">{{ serviceRequirement.text }}</span>
                  </div>
                  <!-- Přílohy požadavku -->
                  <div class="info-table__row row" v-if="serviceRequirement.attachments?.length">
                    <span class="info-table__label col-12">{{ t('serviceRequirementDetail.attachments:') }}</span>
                    <span class="info-table__value col-12">
                      <div class="d-flex flex-wrap gap-2 mt-1">
                        <router-link
                          v-for="att in serviceRequirement.attachments"
                          :key="att.uuid"
                          :to="{ name: 'FileDetail', params: { id: att.id } }"
                          class="d-inline-flex align-items-center gap-1 text-decoration-none small text-body border rounded px-2 py-1"
                        >
                          <i class="mdi mdi-paperclip"></i>
                          {{ att.file_name }}
                        </router-link>
                      </div>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Akce a stav -->
          <div class="col-12 col-md-6">
            <!-- Stavový panel -->
            <div class="card mb-3">
              <div class="card-header">
                <div class="card-header__title">{{ t('serviceRequirementDetail.status_panel') }}</div>
              </div>
              <div class="card-body">
                <!--
                  CSS Grid: dva sloupce — štítek (auto) a ovládací prvky (1fr).
                  Každý řádek gridu sdílí výšku přes oba sloupce → label a controls
                  jsou vždy ve stejné rovině. Oba controls jsou v 1fr sloupci →
                  select má automaticky stejnou šířku jako oblast tlačítek.
                  Pod lg se grid přepne do jednoho sloupce (viz <style scoped>).
                -->
                <div class="status-panel-grid">

                  <!-- Řádek 1: Vyřešen -->
                  <div class="d-flex align-items-center gap-2">
                    <span class="fw-semibold">{{ t('serviceRequirementDetail.resolved') }}:</span>
                    <span v-if="serviceRequirement.is_resolved" class="badge bg-success">
                      {{ t('serviceRequirementDetail.yes') }} — {{ formatDate(serviceRequirement.resolved_at, 'd.m.Y H:i') }}
                    </span>
                    <span v-else class="badge bg-secondary">{{ t('serviceRequirementDetail.no') }}</span>
                  </div>
                  <div class="status-panel-buttons d-flex gap-2">
                    <Button v-if="!serviceRequirement.is_resolved"
                            :label="t('serviceRequirementDetail.mark_resolved')"
                            icon="mdi-check-circle-outline"
                            @click="resolveRequirement"
                    />
                    <Button v-else
                            :label="t('serviceRequirementDetail.reopen')"
                            icon="mdi-refresh"
                            @click="reopenRequirement"
                    />
                    <!-- Tlačítko "Vyřídit" je skryto — workflow původní verze se nadále nepoužívá -->
                    <Button v-if="false"
                            :label="t('serviceRequirementDetail.process')"
                            @click="processServiceRequirement"
                    />
                  </div>

                  <!-- Oddělovač přes oba sloupce -->
                  <hr class="status-panel-divider">

                  <!-- Řádek 2: Stav -->
                  <div class="d-flex align-items-center gap-2">
                    <span class="fw-semibold">{{ t('serviceRequirementDetail.detail_status') }}:</span>
                  </div>
                  <!-- Ikona je absolutně pozicována uvnitř selectu — layout se nehýbe. -->
                  <div class="position-relative">
                    <select
                      class="form-select form-control"
                      :disabled="isChangingStatus"
                      @change="e => changeStatus(e.target.value)"
                    >
                      <option
                        v-for="opt in statusOptions"
                        :key="opt.value"
                        :value="opt.value"
                        :selected="opt.value === serviceRequirement.status"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <!-- right: 2.5rem ponechá prostor pro nativní šipku selectu -->
                    <span
                      v-if="isChangingStatus"
                      class="position-absolute top-50 translate-middle-y mdi mdi-loading mdi-spin text-muted"
                      style="right: 2.5rem; pointer-events: none;"
                    ></span>
                    <span
                      v-else-if="statusSaved"
                      class="position-absolute top-50 translate-middle-y mdi mdi-check text-success"
                      style="right: 2.5rem; pointer-events: none;"
                    ></span>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Historie komunikace -->
      <div class="card mb-4">
        <div class="card-header">
          <div class="card-header__title">{{ t('serviceRequirementDetail.communication') }}</div>
        </div>
        <div class="card-body">
          <div v-if="!serviceRequirement.comments?.length" class="text-muted text-center py-3">
            {{ t('serviceRequirementDetail.no_comments') }}
          </div>
          <div v-else class="d-flex flex-column gap-3">
            <div
                v-for="comment in serviceRequirement.comments"
                :key="comment.id"
                class="p-3 rounded border-start border-3"
                :class="comment.type === 'INTERNAL' ? 'bg-warning bg-opacity-10 border-warning' : 'bg-light'"
                :style="comment.type !== 'INTERNAL' ? { '--bs-border-color': 'var(--primaryColor)' } : {}"
            >
              <div class="d-flex justify-content-between align-items-start mb-1">
                <div class="d-flex align-items-center gap-2">
                  <strong>{{ comment.user?.label ?? comment.user?.name }}</strong>
                  <!-- Interní komentáře jsou barevně označeny -->
                  <span v-if="comment.type === 'INTERNAL'" class="badge bg-warning text-dark">
                                        {{ t('serviceRequirementDetail.internal') }}
                                    </span>
                </div>
                <small class="text-muted">{{ formatDate(comment.created_at, 'd.m.Y H:i') }}</small>
              </div>
              <div>{{ comment.text }}</div>
              <div v-if="comment.attachments?.length" class="mt-2 d-flex flex-wrap gap-2">
                <router-link
                  v-for="att in comment.attachments"
                  :key="att.uuid"
                  :to="{ name: 'FileDetail', params: { id: att.id } }"
                  class="d-inline-flex align-items-center gap-1 text-decoration-none small text-body border rounded px-2 py-1"
                >
                  <i class="mdi mdi-paperclip"></i>
                  {{ att.file_name }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Nový komentář — skryto pokud je požadavek vyřešen -->
      <div v-if="!serviceRequirement.is_resolved" class="card">
        <div class="card-header">
          <div class="card-header__title">{{ t('serviceRequirementDetail.add_comment') }}</div>
        </div>
        <div class="card-body">
          <!-- Přepínač interní / externí -->
          <div class="mb-3 d-flex gap-3">
            <div class="form-check">
              <input class="form-check-input" type="radio" v-model="newCommentType" value="EXTERNAL" id="typeExternal"/>
              <label class="form-check-label" for="typeExternal">
                {{ t('serviceRequirementDetail.external') }}
                <small class="text-muted d-block">{{ t('serviceRequirementDetail.external_hint') }}</small>
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" v-model="newCommentType" value="INTERNAL" id="typeInternal"/>
              <label class="form-check-label" for="typeInternal">
                {{ t('serviceRequirementDetail.internal') }}
                <small class="text-muted d-block">{{ t('serviceRequirementDetail.internal_hint') }}</small>
              </label>
            </div>
          </div>
          <div class="mb-3">
                        <textarea
                            v-model="newCommentText"
                            class="form-control"
                            rows="3"
                            :placeholder="t('serviceRequirementDetail.comment_placeholder')"
                        ></textarea>
          </div>
          <div class="mb-3">
            <input
                type="file"
                multiple
                class="form-control"
                ref="commentFileInput"
                @change="handleFileChange"
            />
          </div>
          <div class="text-end">
            <Button
                :label="isSending ? t('serviceRequirementDetail.sending') : t('serviceRequirementDetail.send_comment')"
                icon="mdi-send"
                :disabled="isSending || !newCommentText.trim()"
                @click="sendComment"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/*
  Dva sloupce: štítek (auto = šíří se obsahem) a ovládací prvky (1fr = zbývající prostor).
  CSS Grid zajišťuje, že oba buňky ve stejném řádku mají vždy stejnou výšku → perfektní
  vertikální zarovnání labelu s tlačítky/selectem.
*/
.status-panel-grid {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 1.5rem;
  row-gap: 0.75rem;
}

/* Oddělovač přes oba sloupce gridu */
.status-panel-divider {
  grid-column: 1 / -1;
  margin: 0;
  opacity: 0.05;
}

/*
  Globální .btn-primary má min-width: 200px !important. Dvě taková tlačítka by
  v 1fr sloupci potřebovala min. 408px — při col-md-6 kartě to způsobuje přetékání.
  Uvnitř panelu min-width přebíjíme, aby tlačítka mohla být libovolně úzká.
  :deep() proniká do child komponenty Button.vue.
*/
.status-panel-grid :deep(.btn-primary) {
  min-width: 0 !important;
}

/*
  flex-shrink: 0 na každém tlačítku ve všech breakpointech — tlačítka se
  nikdy nezúží pod přirozenou šířku, takže text se nikdy nezalomí.
  flex-wrap je odstraněn z kontejneru, proto tlačítka nejdou pod sebe;
  když se nevejdou do 1fr sloupce, přejde celý grid do jednosloupečného režimu.
*/
.status-panel-grid :deep(.btn) {
  flex-shrink: 0;
}

/*
  Pod 1200 px (Bootstrap xl): karta col-md-6 je příliš úzká na to, aby se
  obě tlačítka i select vešla do 1fr sloupce vedle štítků → přepneme na
  jeden sloupec. Tlačítka ani select se tak nikdy nezalamují uvnitř sloupce;
  celý pravý blok prostě přeskočí na nový řádek.
*/
@media (max-width: 1199px) {
  .status-panel-grid {
    grid-template-columns: 1fr;
  }
}

/*
  Ve dvousloupcovém režimu (≥ 1200 px) tlačítka rovnoměrně vyplní 1fr sloupec
  → dohromady jsou stejně široká jako select níže.
*/
@media (min-width: 1200px) {
  .status-panel-grid :deep(.btn) {
    flex-grow: 1;
  }
}
</style>
